import { Injectable } from '@angular/core';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FastTrackApiRoutes } from '@lsq-frontend/fasttrack-shared/app-constants';
import { ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import {
    SendEmailAddressVerificationCodeRequest,
    SendEmailAddressVerificationCodeService,
    SendPhoneNumberVerificationCodeRequest,
    SendPhoneNumberVerificationCodeService,
    ValidatePhoneNumberVerificationCodeService,
    ValidateVerificationCodeRequest,
    ValidateVerificationCodeService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    FieldSchema,
    GroupFieldOption,
    ProspectSchema,
    StageData,
    StageSchema,
    OperationService,
    ProspectService,
    SchemaService,
    ProspectData,
    StringFieldOption,
    DocumentService
} from '@lsq-frontend/remote-services/ar-app';
import { AuthenticationService, Regex } from '@lsq-frontend/common-ui';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import _ from 'lodash';
import { Observable, ReplaySubject } from 'rxjs';
import { GenericJSON, ICreateAccount } from './dr-application-service.interface';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { first } from 'rxjs/operators';
import { HttpClient, HttpRequest } from '@angular/common/http';
import Bugsnag from '@bugsnag/js';

@Injectable({ providedIn: 'root' })
export class DrApplicationService {
    private prospect: ProspectData;
    private prospectSchema: ProspectSchema;
    private _token: string;
    private _visitedSchemaHashId: object = {};
    public prospectSchemaSubject = new ReplaySubject<ProspectSchema>();
    private documentsStageId: string;
    private openRequestTotalDocuments: number;
    public documentCenterName: string = 'DocumentCenter';
    public openRequestName: string = 'openRequest';
    public prospectArTitle: string = 'AR Prospect';
    public prospectScfBuyerTitle: string = 'SCF Buyer Prospect';

    constructor(
        private _prospectService: ProspectService,
        private _schemaService: SchemaService,
        private _operationService: OperationService,
        private _authProviderService: AuthenticationService,
        private _sendEmailAddressVerificationCodeService: SendEmailAddressVerificationCodeService,
        private _validateEmailVerificationCodeService: ValidateVerificationCodeService,
        private _sendPhoneNumberVerificationCodeService: SendPhoneNumberVerificationCodeService,
        private _validatePhoneNumberVerificationCodeService: ValidatePhoneNumberVerificationCodeService,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _alertService: AlertService,
        private _userService: UserService,
        private _documentService: DocumentService,
        private _http: HttpClient
    ) {
        SingletonServiceGuard.register('DrApplicationService');
        this.prospect = {
            id: undefined,
            businessName: undefined,
            prospectSchemaId: undefined,
            coordinatorUserId: undefined,
            status: undefined,
            createdDate: new Date(),
            stages: {}
        };
    }

    public plainFieldSchemas(fieldSchemas: FieldSchema[]): FieldSchema[] {
        let queue = [];
        for (const fieldSchema of fieldSchemas) {
            if (fieldSchema.fieldType === FieldSchema.FieldTypeEnum.GroupField) {
                const GroupFieldInstance = fieldSchema.fieldOption as GroupFieldOption;
                queue = queue.concat(this.plainFieldSchemas(GroupFieldInstance.fieldSchemas));
            } else {
                queue.push(fieldSchema);
            }
        }
        return queue;
    }

    // TODO: add testing
    public stringFieldValidation(fieldSchema: FieldSchema, fieldData: string): boolean {
        let verification = false;
        for (const [key, value] of Object.entries(fieldSchema.fieldOption ?? {})) {
            switch (key) {
                case 'min':
                    verification = fieldData >= value;
                    break;
                case 'max':
                    verification = fieldData <= value;
                    break;
                case 'requiredTrue':
                    verification = !!fieldData;
                    break;
                case 'inputType':
                    if (value === StringFieldOption.InputTypeEnum.Email) {
                        verification = Regex.Email.test(fieldData);
                    } else if (value === StringFieldOption.InputTypeEnum.Tel) {
                        verification = fieldData.toString().length === 10;
                    } else if (value === StringFieldOption.InputTypeEnum.Password) {
                        verification = Regex.StrictPassword.test(fieldData);
                    } else if (value === StringFieldOption.InputTypeEnum.VerificationCode) {
                        verification = Regex.Number.test(fieldData);
                    }
                    break;
                case 'minLength':
                    verification = fieldData.toString().length >= value;
                    break;
                case 'maxLength':
                    verification = fieldData.toString().length <= value;
                    break;
                case 'pattern':
                    verification = new RegExp(value).test(fieldData);
                    break;
                case 'nullValidator':
                    verification = !fieldData;
                    break;
                default:
                    break;
            }
            if (!verification) {
                return false;
            }
        }
        return true;
    }

    // TODO: add test
    public validFieldOptions(fieldSchema: FieldSchema, fieldData: string): boolean {
        switch (fieldSchema.fieldType) {
            case FieldSchema.FieldTypeEnum.StringField.toString():
                return this.stringFieldValidation(fieldSchema, fieldData);
            default:
                return true; // TODO: we could add other field types
        }
    }

    public invalidSchemaProspect(stageSchemaId: string, stage: StageData): boolean {
        const index = this.prospectSchema.stageSchemas.findIndex(
            (stageSchema: StageSchema) => stageSchema.id === stageSchemaId
        );
        if (index === -1) {
            return true;
        } else if (index === this.prospectSchema.stageSchemas.length - 1) {
            return false;
        }
        const stageSchema = this.prospectSchema.stageSchemas[index];
        let passwordIndex = 0;
        let password1: string, password2: string;

        const verifySchema = (fieldSchema: FieldSchema, fieldData: any) => {
            const hasValue = (_fieldSchema: FieldSchema, _fieldData: any) => {
                if (_fieldSchema.fieldType === FieldSchema.FieldTypeEnum.DocumentField) {
                    return !!_fieldData && Array.from(_fieldData).length > 0;
                } else {
                    return !!_fieldData;
                }
            };
            const validFieldOptions = this.validFieldOptions(fieldSchema, fieldData);
            const requiredValidation =
                fieldSchema.isRequired && hasValue(fieldSchema, fieldData) && validFieldOptions;
            const noRequiredValidation =
                !fieldSchema.isRequired && hasValue(fieldSchema, fieldData) && validFieldOptions;
            const noRequiredEmptyValidation =
                !fieldSchema.isRequired && !hasValue(fieldSchema, fieldData);
            return requiredValidation || noRequiredValidation || noRequiredEmptyValidation;
        };

        const verifyFields = (fieldSchemas: FieldSchema[], fields: { [key: string]: any }) => {
            for (const fieldSchema of fieldSchemas) {
                const fieldData = fields[fieldSchema.id];
                if (fieldSchema.fieldType === FieldSchema.FieldTypeEnum.StringField) {
                    const fieldOptions = fieldSchema.fieldOption as StringFieldOption;
                    if (fieldOptions?.inputType === StringFieldOption.InputTypeEnum.Password) {
                        passwordIndex++;
                        switch (passwordIndex) {
                            case 1:
                                password1 = fieldData?.fieldValue;
                                break;
                            case 2:
                                password2 = fieldData?.fieldValue;
                                break;
                        }
                    }
                }
                if (
                    fieldSchema.fieldType === FieldSchema.FieldTypeEnum.GroupField &&
                    fieldSchema.fieldOption
                ) {
                    // TODO: remove this when everything else works
                    const fieldOptions = fieldSchema.fieldOption as GroupFieldOption;
                    return verifyFields(fieldOptions.fieldSchemas, fieldData.fieldValue);
                } else if (!verifySchema(fieldSchema, fieldData?.fieldValue)) {
                    return true;
                }
            }
            return false;
        };
        const fieldSchemas = stageSchema?.fieldSchemas ?? [];
        const fieldsVerification = verifyFields(fieldSchemas, stage?.fields);
        return fieldsVerification || password1 !== password2;
    }

    //@deprecated -- to use in testing
    // public generateProspect(): ProspectData {
    //     if (this.prospectSchema) {
    //         const _createField = (stageId: string) => (fieldSchema: FieldSchema) => {
    //             const labelField = fieldSchema.fieldType === FieldSchema.FieldTypeEnum.LabelField;
    //             if (labelField) {
    //                 return null;
    //             }
    //             if (fieldSchema.fieldType === FieldSchema.FieldTypeEnum.GroupField) {
    //                 const groupFieldOptions = fieldSchema.fieldOption as GroupFieldOption;
    //                 return {
    //                     fieldSchemaId: fieldSchema.id,
    //                     fieldValue: groupFieldOptions.fieldSchemas.map(_createField(stageId))
    //                 };
    //             }
    //             const stage = this.prospect.stages.find(
    //                 (stage: Stage) => stage.stageSchemaId === stageId
    //             );
    //             if (!stage) {
    //                 return {
    //                     fieldSchemaId: fieldSchema.id,
    //                     fieldValue: null
    //                 };
    //             }
    //             const currentProspectField = stage.fields.find(
    //                 (field: any) => field.fieldSchemaId === fieldSchema.id
    //             );
    //             const prospectField: any = {
    //                 fieldSchemaId: fieldSchema.id,
    //                 fieldValue: currentProspectField?.fieldValue
    //             };
    //             return prospectField;
    //         };
    //         const _filterNotNull = (field: any) => field !== null;
    //         const _filterStages = (stageSchema: StageSchema) => {
    //             const prospectStage: Stage = {
    //                 isCompleted: false,
    //                 stageSchemaId: stageSchema.id,
    //                 fields: (stageSchema.fieldSchemas ?? [])
    //                     .map(_createField(stageSchema.id))
    //                     .filter(_filterNotNull)
    //             };
    //             return prospectStage;
    //         };
    //         const prospect: ProspectData = {
    //             id: undefined,
    //             prospectSchemaId: this.prospectSchema.id,
    //             businessName: undefined,
    //             coordinatorUserId: undefined,
    //             stages: this.prospectSchema.stageSchemas.map(_filterStages)
    //         };
    //         return prospect;
    //     }
    //     return null;
    // }

    public isLastStage(stageSchemaId: string): boolean {
        const filterHiddenSchemas = (stageSchema: StageSchema) => !stageSchema.isHiddenToProspect;
        const indexStage = this.prospectSchema.stageSchemas
            .filter(filterHiddenSchemas)
            .findIndex((stageSchema: StageSchema) => stageSchema.id === stageSchemaId);
        return (
            this.prospectSchema.stageSchemas.filter(filterHiddenSchemas).length === indexStage + 1
        );
    }

    public async setProspectByToken(token: string, prospectId: string): Promise<ProspectData> {
        const getProspectByTokenResponse = await this._prospectService.getProspectDataByToken(
            token,
            prospectId
        );
        return getProspectByTokenResponse.toPromise();
    }

    public async setProspectById(prospectId: string): Promise<ProspectData> {
        const getProspectByIdResponse = await this._prospectService.getProspectDataById(prospectId);
        return getProspectByIdResponse.toPromise();
    }

    public async setProspectSchemaBySchemaId(schemaId: string): Promise<ProspectSchema> {
        const getProspectSchemaResponse = await this._schemaService.getProspectSchema(schemaId);
        const schemaData = await getProspectSchemaResponse.toPromise();
        return schemaData[0];
    }

    public async setProspectSchemaByProspectId(
        token: string,
        prospectId: string
    ): Promise<ProspectSchema> {
        const getProspectSchemaResponse = await this._schemaService.getProspectSchemaByToken(
            token,
            prospectId
        );
        return getProspectSchemaResponse.toPromise();
    }

    public async setUp(prospectId: string, token: string): Promise<void> {
        this._token = token;

        if (!prospectId) {
            await this._userService.user$.pipe(first((x) => !!x)).toPromise();
            prospectId = this._userService.getAlternateCompanyKey();
        }

        let prospectData: ProspectData;
        let prospectSchema: ProspectSchema;
        if (!!token && !!prospectId) {
            prospectData = await this.setProspectByToken(token, prospectId);
            prospectSchema = await this.setProspectSchemaByProspectId(token, prospectId);
        } else {
            prospectData = await this.setProspectById(prospectId);
            prospectSchema = await this.setProspectSchemaBySchemaId(prospectData.prospectSchemaId);
        }

        // Always set schema first.
        this.setProspectSchema(prospectSchema);
        this.setProspect(prospectData);
    }

    public async registerUser(
        firstName: string,
        lastName: string,
        businessName: string,
        phone: string,
        email: string,
        password: string
    ): Promise<ProspectData> {
        const registerProspectResponse = this._operationService.registerUserAndProspect(
            firstName,
            lastName,
            businessName,
            phone,
            email,
            password,
            this._token,
            this.prospect.id
        );
        return await registerProspectResponse.toPromise();
    }

    public getProspectSchema(): ProspectSchema {
        return this.prospectSchema;
    }

    public setProspectSchema(prospectSchema: ProspectSchema) {
        this.prospectSchema = prospectSchema;
        this.prospectSchemaSubject.next(this.prospectSchema);
    }

    public getProspect(): ProspectData {
        return this.prospect;
    }

    public setProspect(prospect: ProspectData) {
        this.prospect = prospect;
    }

    public filterStage(stageSchemaId: string): StageData {
        return this.prospect?.stages?.[stageSchemaId];
    }

    public safeUpdateFieldData(
        stageSchemaId: string,
        fieldSchemaId: string,
        prospectUpdated: ProspectData
    ): void {
        this.prospect.stages[stageSchemaId].fields[fieldSchemaId] = _.assignIn(
            this.prospect.stages[stageSchemaId].fields[fieldSchemaId],
            prospectUpdated.stages[stageSchemaId].fields[fieldSchemaId]
        );
    }

    public safeUpdateStageData(stageSchemaId: string, prospectUpdated: ProspectData): void {
        this.prospect.stages[stageSchemaId] = _.assignIn(
            this.prospect.stages[stageSchemaId],
            prospectUpdated.stages[stageSchemaId]
        );
    }

    public async updateStageData(stageSchemaId: string): Promise<void> {
        const prospectId: string = this.prospect.id;
        const stageCopy = { ...this.prospect.stages[stageSchemaId] };
        delete stageCopy.isCompleted;
        const updateProspect = this._prospectService.updateStageData(
            stageCopy,
            prospectId,
            stageSchemaId
        );
        const prospectUpdated = await updateProspect.toPromise();
        this.prospect.stages[stageSchemaId] = _.assignIn(
            this.prospect.stages[stageSchemaId],
            prospectUpdated.stages[stageSchemaId]
        );
    }

    public async submitProspect(): Promise<void> {
        const submitProspect: Observable<ProspectData> = this._operationService.submitProspectData(
            this.prospect.id
        );
        this.prospect = _.assignIn(this.prospect, await submitProspect.toPromise());
    }

    // @deprecated
    public async updateDocument(documentFieldId: string, documentFieldValue: any): Promise<void> {
        const stageSchemaDocument = this.getDocumentsStageID();
        this.prospect.stages[stageSchemaDocument].fields[documentFieldId].fieldValue =
            documentFieldValue;
        await this.updateStageData(stageSchemaDocument);
    }

    public async setPhoneNumber(phoneFieldId: string, phoneFieldValue: string): Promise<void> {
        const stageSchema = 'stage-schema-create-account';
        this.prospect.stages[stageSchema].fields[phoneFieldId].fieldValue = phoneFieldValue;
        await this.updateStageData(stageSchema);
    }

    public mockSubmitApplication(): void {
        const prospectClone: GenericJSON = { ...this.prospect };
        prospectClone.status = ProspectData.StatusEnum.Submitted;
        this.prospect = _.assignIn(this.prospect, prospectClone);
    }

    public get token(): string {
        return this._token;
    }

    public set token(newToken: string) {
        this._token = newToken;
    }

    public async submitCreateAccountStep(createAccountObject: ICreateAccount): Promise<void> {
        const registerUserResponse = await this.registerUser(
            createAccountObject.firstName,
            createAccountObject.lastName,
            createAccountObject.businessName,
            createAccountObject.phone,
            createAccountObject.emailAddress,
            createAccountObject.password
        );
        // If the register call was successful, login.
        if (registerUserResponse !== undefined) {
            const authProviderResult =
                await this._authProviderService.authenticateUserWithEmailAndPassword(
                    createAccountObject.emailAddress,
                    createAccountObject.password
                );

            if (authProviderResult.authorized) {
                await this._userService.authenticateUser(
                    createAccountObject.emailAddress,
                    authProviderResult.accessToken
                );
            } else {
                throw new ErrorEvent('Invalid email address/password. Please try again.');
            }
        }

        this.setProspect(registerUserResponse);
    }

    public async sendEmailAddressVerification(): Promise<void> {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.SendEmailAddressVerificationCode}`,
            ResponseCode.Error
        );
        let retryVerification = 0;
        let errorThrown: boolean;
        while (retryVerification < 5) {
            errorThrown = false;
            try {
                await this._sendEmailAddressVerificationCodeService.executeOnce(
                    new SendEmailAddressVerificationCodeRequest()
                );
            } catch (e) {
                errorThrown = true;
                if (retryVerification === 4) {
                    this._alertService.error(
                        "There's a problem processing the request. Please try again later."
                    );
                }
            }
            if (!errorThrown) {
                break;
            }
            retryVerification++;
        }
    }

    public async verifyEmailAddress(verificationCode: string): Promise<void> {
        // Handle invalid verification code errors here, not in the response interceptor.
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidateEmailAddressVerificationCode}`,
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidateEmailAddressVerificationCode}`,
            ResponseCode.InvalidRequest
        );
        try {
            await this._validateEmailVerificationCodeService.executeOnce(
                new ValidateVerificationCodeRequest({ verificationCode })
            );
        } catch (e) {
            this.displayAndThrowError(
                'Invalid Verification Code. Please try again or request a new code.'
            );
        }
    }

    public async sendMFACode(): Promise<void> {
        const phoneNumber =
            this.prospect.stages['stage-schema-create-account'].fields['field-schema-mobile']
                .fieldValue;
        await this.sendPhoneNumberVerificationCodes(phoneNumber);
    }

    public async sendPhoneNumberVerificationCodes(phoneNumber: string): Promise<void> {
        let retryVerification = 0;
        let errorThrown: boolean;
        while (retryVerification < 5) {
            errorThrown = false;
            try {
                await this._sendPhoneNumberVerificationCodeService.executeOnce(
                    new SendPhoneNumberVerificationCodeRequest({
                        phoneNumber: phoneNumber
                    })
                );
            } catch (e) {
                errorThrown = true;
                if (retryVerification === 4) {
                    this._alertService.error(
                        'Invalid MFA Code. Please try again or request a new code.'
                    );
                }
            }
            if (!errorThrown) {
                break;
            }
            retryVerification++;
        }
    }

    public async verifyPhoneNumber(verificationCode: string): Promise<void> {
        // Handle invalid verification code errors here, not in the response interceptor.
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidatePhoneNumberVerificationCode}`,
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidatePhoneNumberVerificationCode}`,
            ResponseCode.InvalidRequest
        );
        try {
            await this._validatePhoneNumberVerificationCodeService.executeOnce(
                new ValidateVerificationCodeRequest({ verificationCode })
            );
        } catch (e) {
            this.displayAndThrowError('Invalid MFA Code. Please try again or request a new code.');
        }
    }

    public displayAndThrowError(message: string): void {
        this._alertService.error(message);
        throw new Error(message);
    }

    public findStage(stageString: string): StageData {
        return this.prospect.stages[stageString];
    }

    public schemaBlackList(): FieldSchema.FieldTypeEnum[] {
        return [FieldSchema.FieldTypeEnum.LabelField, FieldSchema.FieldTypeEnum.EditPhoneField];
    }

    public initStages(): ProspectData {
        this.setDocumentsStageID(null);
        const stageSchemas: StageSchema[] = this.prospectSchema.stageSchemas;
        for (const stageSchema of stageSchemas) {
            let fields = {};
            if (stageSchema.id in this.prospect.stages) {
                fields = this.prospect.stages[stageSchema.id].fields;
            }
            if (!!stageSchema.fieldSchemas) {
                for (const fieldSchemaId of stageSchema.fieldSchemas) {
                    if (
                        !fields[fieldSchemaId.id] &&
                        !this.schemaBlackList().includes(fieldSchemaId.fieldType)
                    ) {
                        if (fieldSchemaId.fieldType === 'DocumentField') {
                            fields[fieldSchemaId.id] = {
                                fieldValue: []
                            };
                        } else {
                            fields[fieldSchemaId.id] = {
                                fieldValue: ''
                            };
                        }
                    }
                }
            }
            if (this.prospect.stages[stageSchema.id]) {
                this.prospect.stages[stageSchema.id].fields = fields;
            } else {
                this.prospect.stages[stageSchema.id] = { fields };
            }
        }
        return this.prospect;
    }

    public visitField(fieldSchemaId: string): void {
        this._visitedSchemaHashId[fieldSchemaId] = true;
    }

    public isFieldVisited(fieldSchemaId: string): boolean {
        return this._visitedSchemaHashId[fieldSchemaId] || false;
    }

    public async acceptDocument(fieldId: string): Promise<void> {
        const relocateFieldRequest = this._operationService.relocateField(
            this.prospect.id,
            fieldId,
            this.getOpenDocumentsStageID(),
            this.getHistoricalDocumentsStageID()
        );
        try {
            await relocateFieldRequest.toPromise();
            this._alertService.success('Archived successfully');
        } catch (err) {
            this._alertService.error('Failed to archive document.');
        } finally {
            this.reloadData();
        }
    }

    public async downloadDocument(docId: string, docName: string): Promise<void> {
        const downloadDocument = this._documentService.downloadDocument(docId, this.prospect.id);
        const downloadDocResponse = await downloadDocument.toPromise();
        if (!downloadDocResponse || !downloadDocResponse.url) {
            return null;
        }

        try {
            const req = new HttpRequest('GET', downloadDocResponse.url, {
                responseType: 'blob' as 'json'
            });
            const s3DownloadResponse = await this._http.request(req).toPromise();
            const body = (s3DownloadResponse as any)?.body;
            if (body) {
                let binaryData = [body];
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(
                    new Blob(binaryData, { type: body.type })
                );
                downloadLink.target = '_blank';
                downloadLink.setAttribute('download', docName);
                document.body.appendChild(downloadLink);
                downloadLink.click();
            }
        } catch (err) {
            Bugsnag.notify(err);
            throw err;
        }
    }

    public async reloadData(): Promise<void> {
        if (!!!this.prospect || !!!this.prospectSchema) {
            return;
        }
        const getSchemaRequest = this._schemaService.getProspectSchema(
            this.prospectSchema.id,
            false,
            false,
            null
        );
        const getProspectRequest = this._prospectService.getProspectData(
            this.prospect.id,
            null,
            null,
            null,
            null
        );
        try {
            const results = await Promise.all([
                getSchemaRequest.toPromise(),
                getProspectRequest.toPromise()
            ]);
            const prospectSchema = results[0][0];
            const prospect = results[1][0];
            this.setProspect(prospect);
            this.setProspectSchema(prospectSchema);
        } catch (err) {
            this._alertService.error('Failed to retreive data.');
        }
    }

    public getDocumentsStageName(): string {
        const stageSchemaDocument = 'Documents';
        return stageSchemaDocument;
    }

    public getDocumentsStageDefaultID(): string {
        const stageSchemaDocument = 'stage-schema-documents';
        return stageSchemaDocument;
    }

    public getHistoricalDocumentsStageID(): string {
        return 'stage-schema-documents-historical';
    }

    public getOpenDocumentsStageID(): string {
        return 'stage-schema-documents-open';
    }

    public getDocumentsStageID(): string {
        return this.documentsStageId ?? this.getDocumentsStageDefaultID();
    }

    public setDocumentsStageID(documentsStageId: string) {
        this.documentsStageId = documentsStageId;
    }

    public getOpenRequestTotalDocuments() {
        return this.openRequestTotalDocuments;
    }

    public setOpenRequestTotalDocuments(openRequestTotalDocuments: number) {
        this.openRequestTotalDocuments = openRequestTotalDocuments;
    }

    public getTotalOpenStageCount(): void {
        let total = 0;
        let prospectSchema = this.getProspectSchema();
        if (prospectSchema) {
            const openStage = prospectSchema.stageSchemas.filter(
                (stage) => stage.id === this.getOpenDocumentsStageID()
            );
            if (openStage && openStage[0]?.fieldSchemas) {
                total =
                    Object.keys(openStage[0].fieldSchemas).length -
                    this.documentFieldSchemaBlackList.length;
                this.setOpenRequestTotalDocuments(total);
            }
        }
    }

    public documentFieldSchemaBlackList = [
        'field-schema-page-title',
        'field-schema-page-description',
        'field-schema-add-document',
        'field-schema-add-document-list',
        'field-schema-download-all-documents'
    ];
}
